import React from 'react';
import { useTranslation } from 'react-i18next';
import { CurrencyEuroIcon } from '@heroicons/react/24/outline';
import { EpointBalance } from '../../types/epoints';
import { cn } from '../../utils/cn';
import { useCurrency } from '../../contexts/CurrencyContext';

interface BalanceOverviewProps {
  balance?: EpointBalance;
  className?: string;
}

const BalanceOverview: React.FC<BalanceOverviewProps> = ({ balance, className }) => {
  const { t } = useTranslation();
  const { dual } = useCurrency();

  if (!balance) {
    return (
      <div className={cn("bg-white rounded-lg shadow p-6", className)}>
        <div className="animate-pulse">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-32"></div>
              <div className="h-3 bg-gray-200 rounded w-20"></div>
            </div>
            <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
          </div>
          <div className="mt-4 space-y-2">
            <div className="h-8 bg-gray-200 rounded w-24"></div>
            <div className="h-4 bg-gray-200 rounded w-32"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("bg-white rounded-lg shadow p-6", className)}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-secondary">
            {t('balanceOverview.availableEpoints')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('balanceOverview.currentBalance')}
          </p>
        </div>
        <CurrencyEuroIcon className="h-8 w-8 text-green-500" />
      </div>
      
      <div className="mt-4">
        <div className="text-3xl font-bold text-secondary">
          {balance.availablePoints.toLocaleString()}
        </div>
        <div className="text-sm text-gray-500">
          {t('balanceOverview.worth', { amount: dual(balance.availableCashValue) })}
        </div>
      </div>
      
      <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-gray-500">{t('balanceOverview.totalPoints')}</div>
          <div className="font-medium text-secondary">
            {balance.totalPoints.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-gray-500">{t('balanceOverview.pending')}</div>
          <div className="font-medium text-secondary">
            {balance.pendingPoints?.toLocaleString() || '0'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalanceOverview;